/*
 * File: cell-translation-job-status.ts
 * Created: 2026-01-22
 * Author: Val Liu
 */

import { Component, Watch } from "vue-property-decorator";
import { BaseTableBodyCell } from "@root/components/table/core/lib/base-table-body-cell/base-table-body-cell";

type StatusMeta = {
    label: string;
    badgeClass: string;
    iconClass: string;
};

@Component
export default class CellTranslationJobStatus extends BaseTableBodyCell {
    private tickTimer: number | null = null;
    private nowTs = Date.now();

    get statusValue() {
        return String(this.row?.status ?? "").toUpperCase();
    }

    get statusMeta(): StatusMeta {
        switch (this.statusValue) {
            case "COMPLETED":
                return {
                    label: this.$_("w_TranslationJobStatusCompleted"),
                    badgeClass: "badge-completed",
                    iconClass: "fa-check",
                };
            case "FAILED":
                return {
                    label: this.$_("w_TranslationJobStatusFailed"),
                    badgeClass: "badge-failed",
                    iconClass: "fa-times",
                };
            case "STARTING":
                return {
                    label: this.$_("w_TranslationJobStatusStarting"),
                    badgeClass: "badge-processing",
                    iconClass: "fa-sync fa-spin",
                };
            case "TRANSLATING":
            case "FORMATTING":
            case "DETECTING_LANG":
                return {
                    label: this.$_("w_TranslationJobStatusProcessing"),
                    badgeClass: "badge-processing",
                    iconClass: "fa-sync fa-spin",
                };
            case "QUEUED":
            default:
                return {
                    label: this.$_("w_TranslationJobStatusQueued"),
                    badgeClass: "badge-queued",
                    iconClass: "fa-clock",
                };
        }
    }

    get badgeClass() {
        return this.statusMeta.badgeClass;
    }

    get badgeIcon() {
        return this.statusMeta.iconClass;
    }

    get statusText() {
        return this.statusMeta.label;
    }

    get progressPercent() {
        const value = Number(this.row?.progressPercent ?? 0);
        if (Number.isNaN(value)) return 0;
        return Math.max(0, Math.min(100, Math.round(value)));
    }

    get showProgress() {
        return !["COMPLETED", "FAILED"].includes(this.statusValue);
    }

    get showPercent() {
        return this.showProgress && this.progressPercent > 0;
    }

    get progressClass() {
        if (this.statusValue === "QUEUED") return "progress-bar-warning";
        return "progress-bar-tech";
    }

    get startedAtMs() {
        const raw = this.row?.startedAt;
        const ms = raw ? new Date(raw).getTime() : NaN;
        return Number.isFinite(ms) ? ms : null;
    }

    get finishedAtMs() {
        const raw = this.row?.finishedAt;
        const ms = raw ? new Date(raw).getTime() : NaN;
        return Number.isFinite(ms) ? ms : null;
    }

    get isLive() {
        return this.showProgress && this.startedAtMs != null;
    }

    get elapsedLabel() {
        const startedAt = this.startedAtMs;
        if (startedAt == null) return "";
        const endAt =
            this.finishedAtMs != null && !this.isLive
                ? this.finishedAtMs
                : this.nowTs;
        const diffMs = Math.max(0, endAt - startedAt);
        const totalSeconds = Math.floor(diffMs / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        if (hours > 0) return `${hours}h ${minutes}m`;
        if (minutes > 0) return `${minutes}m ${seconds}s`;
        return `${seconds}s`;
    }

    mounted() {
        this.syncTimer();
    }

    beforeDestroy() {
        this.stopTimer();
    }

    @Watch("statusValue")
    onStatusUpdated() {
        this.syncTimer();
    }

    @Watch("row.startedAt")
    onStartUpdated() {
        this.syncTimer();
    }

    private syncTimer() {
        if (this.isLive) {
            if (this.tickTimer == null) {
                this.tickTimer = window.setInterval(() => {
                    this.nowTs = Date.now();
                }, 1000);
            }
        } else {
            this.stopTimer();
        }
    }

    private stopTimer() {
        if (this.tickTimer != null) {
            clearInterval(this.tickTimer);
            this.tickTimer = null;
        }
    }
}
